import React from 'react';
import Icon, {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
  PictureOutlined,
  CheckSquareOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import AttachmentSvg from '../../../../assets/icons/attachment.svg?react';
import { BaseButton } from '../../../../components/UI/BaseButton';
import { QuoteIcon } from '../../../../assets/CustomIcons/QuoteIcon';
import { CodeBlockIcon } from '../../../../assets/CustomIcons/CodeBlockIcon';
import { Heading1Icon } from '../../../../assets/CustomIcons/Heading1';
import { Heading2Icon } from '../../../../assets/CustomIcons/Heading2';
import { Heading3Icon } from '../../../../assets/CustomIcons/Heading3';
import { TextIcon } from '../../../../assets/CustomIcons/Text';
import { ExpandDown } from '../../../../assets/CustomIcons/ExpandDown';
import ToolbarButton from './ToolbarButton';
import { TaskAttachment } from '../../../../types/TaskAttachment';
import { CommentAttachment } from '../../../../types/CommentAttachment';
import colorTokens from '../../../../constants/design_system/colors';
import { TextColorIcon } from '../../../../assets/CustomIcons/TextColor';
import { rgbToHex } from '../../../../utils/textUtils';
import BaseDropdown from '../../../../components/UI/Dropdown/BaseDropdown';
import { AIIcon } from '../../../../assets/CustomIcons/AIIcon';

interface MenuBarProps {
  editor: any;
  onImageUpload: (file: File) => Promise<TaskAttachment | null>;
  onCommentAttachmentUpload: (files: File[]) => Promise<CommentAttachment[]>;
  onAddLink: () => void;
  setIsFileSelecting: (isFileSelecting: boolean) => void;
  isFloating: boolean;
  mode?: 'description' | 'comment';
  onAddSelectionToContext?: (selectedText: string) => void;
  hideImageUpload?: boolean;
}

const MenuBar: React.FC<MenuBarProps> = ({
  editor,
  onImageUpload,
  onCommentAttachmentUpload,
  onAddLink,
  setIsFileSelecting,
  isFloating,
  mode,
  onAddSelectionToContext,
  hideImageUpload,
}) => {
  if (!editor) {
    return null;
  }

  const textColors = [
    { name: 'Default', color: colorTokens.neutral[900] },
    { name: 'Grey', color: colorTokens.neutral[800] },
    { name: 'Yellow', color: colorTokens.yellow[600] },
    { name: 'Red', color: colorTokens.red[600] },
    { name: 'Green', color: colorTokens.green[600] },
    { name: 'Blue', color: colorTokens.blue[600] },
    { name: 'Orange', color: colorTokens.orange[600] },
    { name: 'Purple', color: colorTokens.purple[600] },
  ];

  const handleMenuClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.commands.focus();
  };

  const createImageFileHandler = (accept: string, insertHandler: (attachment: TaskAttachment) => void) => {
    return (e: React.MouseEvent) => {
      handleMenuClick(e);
      setIsFileSelecting(true);
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = accept;
      fileInput.multiple = false;
      fileInput.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          void onImageUpload(file).then((attachment) => {
            if (attachment) {
              insertHandler(attachment);
            }
          });
        }
        setIsFileSelecting(false);
      };
      fileInput.oncancel = () => {
        setIsFileSelecting(false);
      };
      fileInput.click();
    };
  };

  const createCommentAttachmentFileHandler = (
    accept: string,
    insertHandler: (attachments: CommentAttachment[]) => void
  ) => {
    return (e: React.MouseEvent) => {
      handleMenuClick(e);
      setIsFileSelecting(true);
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = accept;
      fileInput.multiple = true;
      fileInput.onchange = (e) => {
        const fileList = (e.target as HTMLInputElement).files;
        if (fileList && fileList.length > 0) {
          const files = Array.from(fileList);

          void onCommentAttachmentUpload(files).then((attachments) => {
            if (attachments && attachments.length > 0) {
              insertHandler(attachments);
            }
          });
        }
        setIsFileSelecting(false);
      };
      fileInput.oncancel = () => {
        setIsFileSelecting(false);
      };
      fileInput.click();
    };
  };

  const insertImage = (attachment: TaskAttachment) => {
    editor.chain().focus().setImage({ src: attachment.url }).run();
  };

  const insertAttachmentLink = (attachments: CommentAttachment[]) => {
    // attachments.forEach((attachment) => {
    //   editor
    //     .chain()
    //     .focus()
    //     .insertContent(`<a href="${attachment.url}" target="_blank">${attachment.filename}</a>`)
    //     .run();
    // });
  };

  const toolbarItems = [
    {
      group: 'heading',
      items: [
        {
          title: 'Heading 1',
          key: 'heading1',
          active: editor.isActive('heading', { level: 1 }),
          icon: <Heading1Icon />,
          onClick: () => {},
        },
        {
          title: 'Heading 2',
          key: 'heading2',
          active: editor.isActive('heading', { level: 2 }),
          icon: <Heading2Icon />,
          onClick: () => {},
        },
        {
          title: 'Heading 3',
          key: 'heading3',
          active: editor.isActive('heading', { level: 3 }),
          icon: <Heading3Icon />,
          onClick: () => {},
        },
        {
          title: 'Normal text',
          key: 'paragraph',
          active: editor.isActive('paragraph'),
          icon: <TextIcon />,
          onClick: () => {},
        },
      ],
    },
    {
      group: 'text',
      items: [
        {
          title: 'Bold',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleBold().run();
          },
          active: editor.isActive('bold'),
          icon: <BoldOutlined />,
        },
        {
          title: 'Italic',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleItalic().run();
          },
          active: editor.isActive('italic'),
          icon: <ItalicOutlined />,
        },
        {
          title: 'Underline',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleUnderline().run();
          },
          active: editor.isActive('underline'),
          icon: <UnderlineOutlined />,
        },
        {
          title: 'Strikethrough',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleStrike().run();
          },
          active: editor.isActive('strike'),
          icon: <StrikethroughOutlined />,
        },
        {
          title: 'Blockquote',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleBlockquote().run();
          },
          active: editor.isActive('blockquote'),
          icon: <QuoteIcon />,
        },
        {
          title: 'Code Block',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleCodeBlock().run();
          },
          active: editor.isActive('codeBlock'),
          icon: <CodeBlockIcon />,
        },
        ...(mode !== 'comment'
          ? [
              {
                title: 'Text Color',
                onClick: (e: React.MouseEvent) => {
                  handleMenuClick(e);
                },
                active: false,
                icon: <TextColorIcon />,
                isColorPicker: true,
              },
            ]
          : []),
      ],
    },
    {
      group: 'lists',
      items: [
        {
          title: 'Bullet list',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleBulletList().run();
          },
          active: editor.isActive('bulletList'),
          icon: <UnorderedListOutlined />,
        },
        {
          title: 'Numbered list',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleOrderedList().run();
          },
          active: editor.isActive('orderedList'),
          icon: <OrderedListOutlined />,
        },
        {
          title: 'Task list',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().toggleTaskList().run();
          },
          active: editor.isActive('taskList'),
          icon: <CheckSquareOutlined />,
        },
      ],
    },
    {
      group: 'media',
      items: [
        {
          title: 'Mention',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            editor.chain().focus().insertContent('@').run();
          },
          active: false,
          icon: <div className="mb-0.5">@</div>,
        },
        ...(isFloating || mode !== 'comment'
          ? []
          : [
              {
                title: 'Add Attachment (max 100MB)',
                onClick: createCommentAttachmentFileHandler(
                  'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.md,.markdown,.zip,.rar,.tar.gz',
                  insertAttachmentLink
                ),
                icon: <Icon component={AttachmentSvg} className="text-neutral-900" />,
              },
            ]),
        {
          title: 'Insert Link',
          onClick: (e: React.MouseEvent) => {
            handleMenuClick(e);
            onAddLink();
          },
          active: editor.isActive('link'),
          icon: <LinkOutlined />,
        },
        ...(isFloating && onAddSelectionToContext
          ? [
              {
                title: 'Reference in AI chat',
                onClick: (e: React.MouseEvent) => {
                  handleMenuClick(e);
                  const { from, to } = editor.state.selection;
                  const selectedText = editor.state.doc.textBetween(from, to, ' ');
                  if (selectedText && selectedText.trim()) {
                    onAddSelectionToContext(selectedText.trim());
                    // Clear the selection and blur the editor to close the floating menu
                    editor.commands.setTextSelection(to);
                    editor.commands.blur();
                  }
                },
                active: false,
                icon: <AIIcon />,
                closeTooltipOnClick: true,
              },
            ]
          : []),
        ...(isFloating || hideImageUpload
          ? []
          : [
              {
                title: 'Insert Image',
                onClick: createImageFileHandler('image/*', insertImage),
                icon: <PictureOutlined />,
              },
            ]),
      ],
    },
  ];

  return (
    <div
      className={`flex gap-2 border-b border-neutral-200 items-center ${mode === 'comment' ? 'px-2' : 'p-2 pt-3'} ${isFloating ? 'h-8' : 'h-14'}`}
      onClick={handleMenuClick}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {toolbarItems.map((group, groupIndex) => (
        <React.Fragment key={group.group}>
          {groupIndex > 0 && <div className="w-px h-6 bg-neutral-300 mx-1" />}
          {group.group === 'heading' && (
            <BaseDropdown
              menu={{
                items: group.items.map((item) => ({
                  label: item.title,
                  key: item.key as string,
                  icon: item.icon,
                })),
                onClick: (e: { key: string }) => {
                  if (e.key === 'paragraph') {
                    editor.chain().setParagraph().run();
                  } else if (e.key === 'heading1') {
                    editor.chain().setHeading({ level: 1 }).run();
                  } else if (e.key === 'heading2') {
                    editor.chain().setHeading({ level: 2 }).run();
                  } else if (e.key === 'heading3') {
                    editor.chain().setHeading({ level: 3 }).run();
                  }
                },
              }}
              trigger={['click']}
            >
              <BaseButton type="secondary" size="small" className="w-12">
                {editor.isActive('paragraph') && <TextIcon />}
                {editor.isActive('heading', { level: 1 }) && <Heading1Icon />}
                {editor.isActive('heading', { level: 2 }) && <Heading2Icon />}
                {editor.isActive('heading', { level: 3 }) && <Heading3Icon />}
                <Icon component={ExpandDown} />
              </BaseButton>
            </BaseDropdown>
          )}
          {group.group !== 'heading' &&
            group.items.map((item: any) => {
              // Special handling for color picker
              if (item.isColorPicker) {
                return (
                  <BaseDropdown
                    key={item.title}
                    menu={{
                      items: textColors.map((colorOption) => {
                        const editorColor = editor.getAttributes('textStyle').color;
                        const normalizedEditorColor = editorColor ? rgbToHex(editorColor).toLowerCase() : null;
                        const normalizedOptionColor = colorOption.color.toLowerCase();

                        const isSelected =
                          normalizedEditorColor === normalizedOptionColor ||
                          (!editorColor && colorOption.color === colorTokens.neutral[900]);
                        return {
                          label: (
                            <div
                              className="flex items-center justify-between gap-2 w-[200px]"
                              style={{ color: colorOption.color }}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colorOption.color }} />
                                {colorOption.name}
                              </div>
                              {isSelected && <CheckOutlined />}
                            </div>
                          ),
                          key: colorOption.color,
                        };
                      }),
                      onClick: (e) => {
                        editor.commands.focus();
                        if (e.key === colorTokens.neutral[900]) {
                          // Reset to default color
                          editor.chain().focus().unsetColor().run();
                        } else {
                          editor.chain().focus().setColor(e.key).run();
                        }
                      },
                    }}
                    trigger={['click']}
                  >
                    <BaseButton type="secondary" size="small" className="w-12" active={false}>
                      <div style={{ color: editor.getAttributes('textStyle').color || colorTokens.neutral[900] }}>
                        <TextColorIcon />
                      </div>
                      <Icon component={ExpandDown} />
                    </BaseButton>
                  </BaseDropdown>
                );
              }

              return (
                <ToolbarButton
                  key={item.title}
                  title={item.title}
                  onClick={item.onClick}
                  active={item.active}
                  icon={item.icon}
                  isLinkMenu={true}
                  closeTooltipOnClick={item.closeTooltipOnClick}
                />
              );
            })}
        </React.Fragment>
      ))}
    </div>
  );
};

export default MenuBar;
